import { z } from 'zod';
import { amountSchema, nonNegativeAmountInputSchema } from '../common/amount.js';
import { branchRefSchema, employeeRefSchema } from '../common/refs.js';
import { linkStatusSchema } from './common.js';
import { debtorProfileIssueSchema } from './profile.js';
/** حساب ذمم: a named running account of a debtor, in one branch (debts.md section 6.4). */
export const ACCOUNT_NAME_MIN = 2;
export const ACCOUNT_NAME_MAX = 100;
/** The default name of a migrated account the grouping workbook leaves unnamed (glossary: ذمم عامة). */
export const DEFAULT_ACCOUNT_NAME = 'ذمم عامة';
/** اسم الحساب: trimmed, inner spaces collapsed, 2-100 characters, otherwise free text. */
export const accountNameSchema = z
    .string()
    .transform((v) => v.trim().replace(/\s+/g, ' '))
    .pipe(z.string().min(ACCOUNT_NAME_MIN).max(ACCOUNT_NAME_MAX));
/** The comparison key of the uniqueness rule: spaces at the ends and letter case ignored. */
export function accountNameKey(name) {
    return name.trim().toLowerCase();
}
/** سقف الدين: optional, >= 0, up to five decimals; null or empty means no limit. */
export const debtLimitInputSchema = z
    .union([nonNegativeAmountInputSchema, z.literal(''), z.null()])
    .transform((v) => (v === '' ? null : v));
/** إضافة حساب for an existing debtor (POST /debtors/:id/accounts). */
export const createAccountSchema = z.object({
    name: accountNameSchema,
    branchId: z.string().trim().min(1),
    debtLimit: debtLimitInputSchema.optional(),
});
/** تعديل الحساب. The balance is never edited here (debts.md section 6.4). */
export const updateAccountSchema = z
    .object({
    name: accountNameSchema.optional(),
    branchId: z.string().trim().min(1).optional(),
    debtLimit: debtLimitInputSchema.optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((field) => field !== undefined), {
    message: 'at least one field is required',
});
/** The debtor an account belongs to, as shown in the account statement header. */
export const accountDebtorSchema = z.object({
    id: z.string(),
    name: z.string(),
    phone: z.string().nullable(),
    customerId: z.string().nullable(),
    linkStatus: linkStatusSchema,
    /** Movements are blocked while not empty (debts.md section 22.2). */
    profileIssues: z.array(debtorProfileIssueSchema),
});
export const accountSchema = z.object({
    id: z.string(),
    number: z.number().int(),
    legacyId: z.number().int().nullable(),
    /** True for an account brought over from the legacy system. */
    migrated: z.boolean(),
    /** اسم الحساب. */
    name: z.string(),
    debtor: accountDebtorSchema,
    legacyPhone: z.string().nullable(),
    branch: branchRefSchema,
    debtLimit: amountSchema.nullable(),
    balance: amountSchema,
    lastMovementAt: z.string().nullable(),
    /** Null for migrated accounts ("—"). */
    createdBy: employeeRefSchema.nullable(),
    /** Null when legacy had no creation date ("—"). */
    createdAt: z.string().nullable(),
    updatedAt: z.string(),
});
/** مجموع المدين (positive balances), مجموع الدائن (negative, shown negative), الرصيد (net). */
export const accountTotalsSchema = z.object({
    debit: amountSchema,
    credit: amountSchema,
    net: amountSchema,
});
//# sourceMappingURL=accounts.js.map